import React, { useState, useEffect } from 'react';
import { getOrders, updateOrder, getOrderDetails } from '../api';

function OrderManagement({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getOrders(user.id);
      if (result.success) {
        setOrders(result.orders || []);
      } else {
        setError(result.error || 'Fehler beim Laden der Bestellungen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = async (order) => {
    try {
      const result = await getOrderDetails(user.id, order.id);
      if (result.success) {
        setEditingOrder(result.order);
        setOrderStatus(result.order.status);
        setTrackingNumber(result.order.tracking_number || '');
        setOrderNotes(result.order.notes || '');
      }
    } catch (err) {
      setError('Fehler beim Laden der Bestelldetails: ' + err.message);
    }
  };

  const handleSaveOrder = async (e) => {
    e.preventDefault();
    if (!editingOrder) return;

    try {
      const updates = {};
      if (orderStatus) updates.status = orderStatus;
      if (trackingNumber !== undefined) updates.tracking_number = trackingNumber;
      if (orderNotes !== undefined) updates.notes = orderNotes;

      const result = await updateOrder(user.id, editingOrder.id, updates);
      if (result.success) {
        setEditingOrder(null);
        setOrderStatus('');
        setTrackingNumber('');
        setOrderNotes('');
        loadOrders();
      } else {
        setError(result.error || 'Fehler beim Aktualisieren der Bestellung');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#10b981',
      cancelled: '#ef4444'
    };
    return colors[status] || '#64748b';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      processing: 'In Bearbeitung',
      shipped: 'Versendet',
      delivered: 'Geliefert',
      cancelled: 'Storniert'
    };
    return labels[status] || status;
  };

  const containerStyles = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  };

  const headerStyles = {
    marginBottom: '24px',
  };

  const buttonStyles = {
    padding: '10px 20px',
    backgroundColor: '#4a6de5',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  };

  const cardStyles = {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  };

  if (loading) {
    return (
      <div style={containerStyles}>
        <div style={{ textAlign: 'center', padding: '40px' }}>Laden...</div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <h1 style={{ margin: 0 }}>Bestellverwaltung</h1>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {editingOrder && (
        <div style={cardStyles}>
          <h3 style={{ marginTop: 0 }}>Bestellung bearbeiten: {editingOrder.order_number}</h3>
          <form onSubmit={handleSaveOrder}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Status</label>
              <select
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                required
              >
                <option value="pending">Ausstehend</option>
                <option value="processing">In Bearbeitung</option>
                <option value="shipped">Versendet</option>
                <option value="delivered">Geliefert</option>
                <option value="cancelled">Storniert</option>
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Tracking-Nummer</label>
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                placeholder="z.B. DHL123456789"
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Notizen</label>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', minHeight: '80px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" style={buttonStyles}>
                Speichern
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingOrder(null);
                  setOrderStatus('');
                  setTrackingNumber('');
                  setOrderNotes('');
                }}
                style={{ ...buttonStyles, backgroundColor: '#64748b' }}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Noch keine Bestellungen vorhanden.
        </div>
      ) : (
        orders.map((order) => (
          <div key={order.id} style={cardStyles}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>Bestellung {order.order_number}</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                  Erstellt am: {new Date(order.created_at).toLocaleDateString('de-DE')}
                  {order.agentur_name && ` • Agentur: ${order.agentur_name}`}
                  {order.created_by_name && ` • Erstellt von: ${order.created_by_name}`}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <span
                  style={{
                    padding: '6px 12px',
                    backgroundColor: getStatusColor(order.status),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                >
                  {getStatusLabel(order.status)}
                </span>
                <button
                  onClick={() => handleEditOrder(order)}
                  style={{ padding: '6px 12px', backgroundColor: '#4a6de5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                >
                  Bearbeiten
                </button>
              </div>
            </div>

            {order.address && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Lieferadresse:</strong>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', whiteSpace: 'pre-line' }}>{order.address}</p>
              </div>
            )}
            {order.tracking_number && (
              <p style={{ marginBottom: '8px' }}>
                <strong>Tracking-Nummer:</strong> {order.tracking_number}
              </p>
            )}
            {order.notes && (
              <p style={{ marginBottom: '12px', color: '#64748b' }}>
                <strong>Notizen:</strong> {order.notes}
              </p>
            )}
            <h4 style={{ marginBottom: '8px' }}>Bestellpositionen:</h4>
            <div>
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={index} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <strong>{item.item_name}</strong>
                    {item.variant_name && (
                      <span style={{ marginLeft: '8px', color: '#64748b' }}>
                        ({item.variant_name})
                      </span>
                    )}
                    <span style={{ marginLeft: '12px', color: '#64748b' }}>
                      Menge: {item.quantity}
                    </span>
                  </div>
                ))
              ) : (
                <p style={{ color: '#64748b' }}>Keine Positionen gefunden</p>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default OrderManagement;
